import { useEffect, useState } from "react";
import { CalendarPlus, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label, Select, Textarea } from "@/components/ui/field";
import { Badge } from "@/components/ui/badge";
import type { CommunityEvent, Registration } from "@/shared/types";

export function EventsAdminPage() {
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [selected, setSelected] = useState<CommunityEvent | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [creating, setCreating] = useState(false);
  const load = () => api<CommunityEvent[]>("/admin/events").then(setEvents);
  useEffect(() => {
    void load();
  }, []);
  const open = async (event: CommunityEvent) => {
    setSelected(event);
    setRegistrations(
      await api<Registration[]>(`/admin/events/${event.id}/registrations`),
    );
  };
  const create = async (formEvent: React.FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    const data = new FormData(formEvent.currentTarget);
    const start = new Date(String(data.get("starts_at"))).toISOString();
    await api("/admin/events", {
      method: "POST",
      body: JSON.stringify({
        location_id: "mountain-view",
        slug: data.get("slug"),
        title: { "en-US": data.get("title_en"), "es-US": data.get("title_es") },
        description: {
          "en-US": data.get("description_en"),
          "es-US": data.get("description_es"),
        },
        starts_at: start,
        ends_at: new Date(
          new Date(start).getTime() + 2 * 60 * 60 * 1000,
        ).toISOString(),
        timezone: "America/Los_Angeles",
        venue_name: data.get("venue"),
        event_type: data.get("event_type"),
        registration_open: true,
        questions: [],
      }),
    });
    setCreating(false);
    await load();
  };
  const checkIn = async (registration: Registration) => {
    const updated = await api<Registration>(
      `/admin/registrations/${registration.id}/check-in`,
      { method: "POST" },
    );
    setRegistrations((all) =>
      all.map((item) => (item.id === updated.id ? updated : item)),
    );
  };
  return (
    <div className="mx-auto max-w-7xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Events</h1>
          <p className="mt-2 text-muted-foreground">
            Plan, register, and record who actually attended.
          </p>
        </div>
        <Button onClick={() => setCreating(!creating)}>
          <CalendarPlus size={17} />
          New event
        </Button>
      </div>
      {creating && (
        <Card className="mt-6">
          <CardContent className="p-6">
            <form onSubmit={create} className="grid gap-4 md:grid-cols-2">
              <div>
                <Label className="mb-2 block">English title</Label>
                <Input name="title_en" required />
              </div>
              <div>
                <Label className="mb-2 block">Spanish title</Label>
                <Input name="title_es" />
              </div>
              <div>
                <Label className="mb-2 block">Slug</Label>
                <Input name="slug" required />
              </div>
              <div>
                <Label className="mb-2 block">Starts</Label>
                <Input name="starts_at" type="datetime-local" required />
              </div>
              <div>
                <Label className="mb-2 block">Venue</Label>
                <Input name="venue" required />
              </div>
              <div>
                <Label className="mb-2 block">Type</Label>
                <Select name="event_type">
                  <option value="special_event">Special event</option>
                  <option value="distribution">Distribution</option>
                  <option value="volunteer">Volunteer</option>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">English description</Label>
                <Textarea name="description_en" />
              </div>
              <div>
                <Label className="mb-2 block">Spanish description</Label>
                <Textarea name="description_es" />
              </div>
              <div className="md:col-span-2">
                <Button type="submit">Publish registration</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
      <div className="mt-7 grid gap-6 lg:grid-cols-[1fr_420px]">
        <div className="space-y-4">
          {events.map((event) => (
            <Card
              key={event.id}
              className="cursor-pointer hover:border-primary/40"
              onClick={() => void open(event)}
            >
              <CardContent className="flex items-center justify-between p-5">
                <div>
                  <strong>{event.title["en-US"]}</strong>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {new Date(event.starts_at).toLocaleString()} ·{" "}
                    {event.venue_name}
                  </p>
                </div>
                <Badge>{event.event_type.replace("_", " ")}</Badge>
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="h-fit">
          <CardHeader>
            <CardTitle>
              {selected
                ? `${selected.title["en-US"]} registrations`
                : "Registrations"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selected ? (
              <div className="space-y-3">
                {registrations.map((registration) => (
                  <div
                    key={registration.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div>
                      <strong className="text-sm">
                        Party of {registration.party_size}
                      </strong>
                      <p className="text-xs text-muted-foreground">
                        {registration.status}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant={
                        registration.status === "attended"
                          ? "secondary"
                          : "default"
                      }
                      disabled={registration.status === "attended"}
                      onClick={() => void checkIn(registration)}
                    >
                      <Check size={15} />
                      {registration.status === "attended"
                        ? "Checked in"
                        : "Check in"}
                    </Button>
                  </div>
                ))}
                {!registrations.length && (
                  <p className="text-sm text-muted-foreground">
                    No registrations yet.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Select an event to review registrations and check in households.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
